import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { format, differenceInCalendarDays } from "date-fns";
import { AlertTriangle, Copy, Sparkles } from "lucide-react";

import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useHasCapability } from "@/hooks/useCapabilities";
import { fetchLatestFrontendDigest } from "@/api/frontendObservability";
import {
  FrontendErrorLog,
  FrontendErrorSeverity,
  FrontendErrorDigest,
  severityOptions,
  severityStyles,
} from "@/observability/frontendTypes";

const PAGE_SIZE = 25;
const FRONTEND_OBSERVABILITY_CAPABILITY = "observability.frontend";

interface FrontendErrorLogResponse {
  items: FrontendErrorLog[];
  total: number;
}

interface FrontendErrorQuery {
  page: number;
  limit: number;
  route?: string;
  severity?: FrontendErrorSeverity;
  version?: string;
}

type SeverityFilterValue = FrontendErrorSeverity | "all";

const fetchFrontendErrors = async ([, params]: readonly [string, FrontendErrorQuery]): Promise<FrontendErrorLogResponse> => {
  const search = new URLSearchParams();
  search.set("limit", String(params.limit));
  search.set("offset", String(params.page * params.limit));
  if (params.route) search.set("route", params.route);
  if (params.severity) search.set("severity", params.severity);
  if (params.version) search.set("version", params.version);

  return api<FrontendErrorLogResponse>(`/observability/frontend?${search.toString()}`);
};

function useDebouncedValue<T>(value: T, delay = 400): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

function formatTimestamp(value: string): string {
  try {
    return format(new Date(value), "MMM d, HH:mm:ss");
  } catch {
    return value;
  }
}

export default function FrontendErrorsPage() {
  const [routeInput, setRouteInput] = useState("");
  const [versionInput, setVersionInput] = useState("");
  const [severity, setSeverity] = useState<SeverityFilterValue>("all");
  const [page, setPage] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [digestOpen, setDigestOpen] = useState(false);
  const [selected, setSelected] = useState<FrontendErrorLog | null>(null);
  const hasCapability = useHasCapability(FRONTEND_OBSERVABILITY_CAPABILITY);
  const canViewDigest = useHasCapability("observability.digest");

  const debouncedRoute = useDebouncedValue(routeInput.trim(), 400);

  const query = useMemo<FrontendErrorQuery>(
    () => ({
      page,
      limit: PAGE_SIZE,
      route: debouncedRoute || undefined,
      severity: severity === "all" ? undefined : (severity as FrontendErrorSeverity),
      version: versionInput.trim() || undefined,
    }),
    [page, debouncedRoute, severity, versionInput],
  );

  useEffect(() => {
    setPage(0);
  }, [debouncedRoute, severity, versionInput]);

  const swrKey = useMemo(() => (hasCapability ? (["observability/frontend", query] as const) : null), [hasCapability, query]);

  const { data, error, isLoading, mutate } = useSWR<FrontendErrorLogResponse, Error>(swrKey, fetchFrontendErrors);
  const {
    data: digest,
    error: digestError,
    isLoading: digestLoading,
  } = useSWR<FrontendErrorDigest | null>(canViewDigest ? "/observability/frontend/digest/latest" : null, fetchLatestFrontendDigest);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const canPrev = page > 0;
  const canNext = (page + 1) * PAGE_SIZE < total;
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, (page + 1) * PAGE_SIZE);
  const digestDate = useMemo(() => (digest?.digest_date ? new Date(digest.digest_date) : null), [digest]);
  const digestReady = digestDate ? differenceInCalendarDays(new Date(), digestDate) === 1 : false;
  const digestDateLabel = digestDate ? format(digestDate, "MMM d, yyyy") : null;
  const digestSummary = digest?.summary;
  const digestWindowStart =
    digestSummary?.windowStart && !Number.isNaN(Date.parse(digestSummary.windowStart))
      ? format(new Date(digestSummary.windowStart), "MMM d, HH:mm")
      : null;
  const digestWindowEnd =
    digestSummary?.windowEnd && !Number.isNaN(Date.parse(digestSummary.windowEnd))
      ? format(new Date(digestSummary.windowEnd), "MMM d, HH:mm")
      : null;
  const digestNewCount = digestSummary?.newVsRecurring?.new ?? 0;
  const digestRecurringCount = digestSummary?.newVsRecurring?.recurring ?? 0;
  const digestWindowSummary =
    digestWindowStart && digestWindowEnd
      ? `${digestWindowStart} – ${digestWindowEnd}`
      : digestDateLabel ?? "last 24 hours";
  const digestRecipientCount = digest?.recipients?.length ?? 0;
  const handleOpenDigest = () => {
    if (digest) {
      setDigestOpen(true);
    }
  };

  const handleSelectLog = (log: FrontendErrorLog) => {
    setSelected(log);
    setDetailOpen(true);
  };

  const handleCopy = () => {
    if (!selected) return;
    const payload = JSON.stringify(selected, null, 2);
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(payload).catch(() => {
        // ignore clipboard failures
      });
    }
  };

  if (!hasCapability) {
    return null;
  }

  return (
    <div className="aa-page-shell space-y-6">
      <div>
        <h1 className="text-3xl font-semibold text-foreground">Frontend Errors</h1>
        <p className="text-sm text-muted-foreground">
          Captured client-side errors from the Americana Agents interface.
        </p>
      </div>

      {canViewDigest && digestReady && digest && (
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-3">
          <div className="flex items-center gap-3 text-sm text-emerald-100">
            <Sparkles className="h-5 w-5" />
            <div>
              <p className="font-medium">Yesterday&apos;s digest is ready</p>
              <p className="text-xs text-emerald-200">Snapshot for {digestWindowSummary}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleOpenDigest} className="border-emerald-400 text-emerald-100">
            View digest
          </Button>
        </div>
      )}

      <div className="aa-glass-card flex flex-wrap gap-4 rounded-xl p-4">
        <div className="flex flex-col gap-1">
          <span className="text-sm text-muted-foreground">Total events</span>
          <span className="text-2xl font-semibold text-foreground">{total.toLocaleString()}</span>
        </div>
        <div className="flex flex-1 flex-wrap gap-4">
          <div className="flex min-w-[200px] flex-1 flex-col gap-2">
            <label htmlFor="route-filter" className="text-xs uppercase text-muted-foreground">
              Route
            </label>
            <Input
              id="route-filter"
              placeholder="/dashboard"
              value={routeInput}
              onChange={(event) => setRouteInput(event.target.value)}
            />
          </div>
          <div className="flex min-w-[160px] flex-1 flex-col gap-2">
            <label className="text-xs uppercase text-muted-foreground">Severity</label>
            <Select value={severity} onValueChange={(value) => setSeverity(value as SeverityFilterValue)}>
              <SelectTrigger aria-label="Severity filter">
                <SelectValue placeholder="All severities" />
              </SelectTrigger>
              <SelectContent>
                {severityOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex min-w-[200px] flex-1 flex-col gap-2">
            <label htmlFor="version-filter" className="text-xs uppercase text-muted-foreground">
              Version
            </label>
            <Input
              id="version-filter"
              placeholder="git SHA or build"
              value={versionInput}
              onChange={(event) => setVersionInput(event.target.value)}
            />
          </div>
        </div>
        {canViewDigest && (
          <div className="flex flex-col items-start justify-end gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenDigest}
              disabled={digestLoading || !digest}
              className="whitespace-nowrap"
            >
              View yesterday&apos;s digest
            </Button>
            {digestError && (
              <span className="text-xs text-primary">Digest unavailable: {digestError.message}</span>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center justify-between rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            <span>Unable to load frontend errors: {error.message}</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => mutate()}>
            Retry
          </Button>
        </div>
      )}

      <div className="aa-glass-card rounded-xl">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-40">Time</TableHead>
              <TableHead className="w-28">Severity</TableHead>
              <TableHead className="w-48">Route</TableHead>
              <TableHead>Message</TableHead>
              <TableHead className="w-32">Version</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Skeleton className="h-16 w-full rounded-lg bg-muted/40" />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !items.length && (
              <TableRow>
                <TableCell colSpan={5}>
                  <div className="py-8 text-center text-sm text-muted-foreground">No frontend errors found.</div>
                </TableCell>
              </TableRow>
            )}
            {items.map((log) => (
              <TableRow
                key={log.id}
                className="cursor-pointer transition hover:bg-background/5"
                onClick={() => handleSelectLog(log)}
              >
                <TableCell className="font-medium">{formatTimestamp(log.created_at)}</TableCell>
                <TableCell>
                  <Badge className={cn("capitalize", severityStyles[log.severity])}>{log.severity}</Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{log.route || "—"}</TableCell>
                <TableCell className="max-w-[420px] truncate text-sm">{log.message}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {log.version ? (
                    <Badge variant="outline" className="text-xs uppercase tracking-wide">
                      build {log.version}
                    </Badge>
                  ) : (
                    "—"
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/5 px-4 py-3 text-sm text-muted-foreground">
          <span>
            Showing {rangeStart}-{rangeEnd} of {total.toLocaleString()}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={!canPrev} onClick={() => setPage((prev) => Math.max(prev - 1, 0))}>
              Previous
            </Button>
            <span className="text-xs text-muted-foreground">
              Page {Math.min(page + 1, totalPages)} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={!canNext}
              onClick={() => setPage((prev) => (canNext ? prev + 1 : prev))}
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      <Sheet
        open={detailOpen}
        onOpenChange={(open) => {
          setDetailOpen(open);
          if (!open) {
            setSelected(null);
          }
        }}
      >
        <SheetContent className="max-w-2xl overflow-y-auto bg-background text-foreground">
          <SheetHeader>
            <SheetTitle className="text-xl">Frontend error</SheetTitle>
            <SheetDescription>Detailed payload for the selected client-side error.</SheetDescription>
          </SheetHeader>
          {selected ? (
            <div className="space-y-4 py-4">
              <div>
                <p className="text-sm text-muted-foreground">Message</p>
                <p className="text-base font-medium text-foreground">{selected.message}</p>
              </div>
              <div className="grid gap-3 rounded-lg border border-border/40 bg-background/40 p-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Occurred</span>
                  <span>{formatTimestamp(selected.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Route</span>
                  <span>{selected.route || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Severity</span>
                  <Badge className={cn("capitalize", severityStyles[selected.severity])}>{selected.severity}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Version</span>
                  {selected.version ? (
                    <Badge variant="outline" className="text-xs">
                      build {selected.version}
                    </Badge>
                  ) : (
                    <span>—</span>
                  )}
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">User</span>
                  <span>{selected.user_id || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Component</span>
                  <span>{selected.component_name || "—"}</span>
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Stack trace</p>
                <pre className="mt-2 max-h-60 overflow-auto rounded-lg border border-border bg-black/50 p-3 text-xs text-foreground">
                  {selected.stack || "No stack trace provided."}
                </pre>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-foreground">Metadata</p>
                  <Button variant="outline" size="sm" onClick={handleCopy}>
                    <Copy className="mr-2 h-4 w-4" />
                    Copy error JSON
                  </Button>
                </div>
                <pre className="mt-2 max-h-60 overflow-auto rounded-lg border border-border bg-black/50 p-3 text-xs text-emerald-100">
                  {selected.metadata ? JSON.stringify(selected.metadata, null, 2) : "No metadata provided."}
                </pre>
              </div>
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">Select an error to view the payload.</p>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={digestOpen} onOpenChange={setDigestOpen}>
        <DialogContent className="max-w-3xl overflow-y-auto bg-background text-foreground">
          <DialogHeader>
            <DialogTitle className="text-xl">
              Frontend digest {digestDateLabel ? `— ${digestDateLabel}` : ""}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Snapshot for {digestWindowSummary}
              {digestRecipientCount > 0 ? ` • Delivered to ${digestRecipientCount} recipients` : ""}
            </DialogDescription>
          </DialogHeader>
          {digest ? (
            <div className="space-y-4 text-sm">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="rounded-xl border border-border/10 bg-background/5 p-4">
                  <p className="text-xs uppercase text-muted-foreground">Errors (24h)</p>
                  <p className="text-2xl font-semibold text-foreground">{digest.total_errors.toLocaleString()}</p>
                </div>
                <div className="rounded-xl border border-border/10 bg-background/5 p-4">
                  <p className="text-xs uppercase text-muted-foreground">Unique users</p>
                  <p className="text-2xl font-semibold text-foreground">{digest.unique_users.toLocaleString()}</p>
                </div>
                <div className="rounded-xl border border-border/10 bg-background/5 p-4">
                  <p className="text-xs uppercase text-muted-foreground">New vs recurring</p>
                  <p className="text-2xl font-semibold text-foreground">
                    {digestNewCount} / {digestRecurringCount}
                  </p>
                </div>
              </div>
              <div className="rounded-xl border border-border/10 bg-black/40 p-4">
                <div
                  className="prose prose-invert max-w-none text-foreground"
                  dangerouslySetInnerHTML={{ __html: digest.html_body }}
                />
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No digest available yet.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
